import type { PostTheme, TPostContentElement } from '@shared/dream-components';
import { PaywallElement, PostContentElement } from '@shared/dream-components';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useRenderBlueskyEmbeds from '~/hooks/useRenderBlueskyEmbeds';
import useRenderStocktwitsEmbeds from '~/hooks/useRenderStocktwitsEmbeds';
import useRenderTiktokEmbeds from '~/hooks/useRenderTiktokEmbeds';
import useRenderTwitterEmbeds from '~/hooks/useRenderTwitterEmbeds';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';

import { useOutletContext, useRouteLoaderData } from '@remix-run/react';
import type { PostPageContextType } from '~/context/PostPageContext';
import { usePostPageContext } from '~/context/usePostPageContext';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';
import rewriteSrc from '~/utils/rewriteSrc';
import { PostAudio } from '../PostAudio';
import { ChildDecoratorComponent } from './ChildDecoratorComponent';
import { type ProcessorFunction, type ProcessorFunctionOptions, process, processors } from './processors';

interface Props {
  node: TPostContentElement;
  children: ReactNode | ReactNode[];
}

const PostContent = ({ node, children }: Props) => {
  const { post, postMergeTags, site, paywall, onlyRenderComments, previewSubscription } =
    useTypedLoaderData<PostLoaderData>();

  const { t } = useTranslation();
  const { isLoggedIn } = useOutletContext<OutletContext>();

  const {
    isGatedContentDisabled,
    showSubscriberBreak,
    showPaywallBreak,
    showPaywallAndHidePostContent,
    showTextToSpeechBlock,
    paywallUrl,
    utmParams,
  } = usePostPageContext() as PostPageContextType;
  const postTheme = post.post_theme;

  useRenderTiktokEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderTwitterEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderBlueskyEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);
  useRenderStocktwitsEmbeds(post.slug, isGatedContentDisabled && !onlyRenderComments);

  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const currentSubscription = subscription || previewSubscription;

  const processorOptions: ProcessorFunctionOptions = useMemo(() => {
    const enforcePaywallBreaks = post.enforce_paywall_breaks;
    const pollsEnabled = site.publication.has_polls;
    const subscriptionTierIds = (currentSubscription?.premium_tiers || []).map((tier) => tier.id);

    return {
      subscriberBreakElementData: {
        title: post?.email_capture_title,
        message: post?.email_capture_message,
        cta: post?.email_capture_cta,
        is_publication_private: !!site?.publication?.private,
        site_settings: {
          terms_of_service_enabled: site?.viewable_site_version?.settings?.terms_of_service_enabled,
          terms_of_service: site?.viewable_site_version?.settings?.terms_of_service,
        },
      },
      hasSubscription: !!currentSubscription,
      isPremiumSubscriber: currentSubscription?.tier === 'premium',
      referralsCount: currentSubscription?.referrals_count,
      subscriptionTierIds,
      shouldForcePremium: post?.force_premium,
      postMergeTags,
      pollsEnabled,
      enforcePaywallBreaks,
      subscriberId: currentSubscription?.subscriber_id,
      subscriberEmail: currentSubscription?.email,
    };
  }, [site, post, currentSubscription, post?.force_premium, postMergeTags]);

  const processorList: ProcessorFunction[] = [];

  if (processorOptions?.postMergeTags && Object.keys(processorOptions.postMergeTags).length > 0) {
    processorList.unshift(processors.processMergeTags);
  }

  processorList.unshift(processors.processData);

  if (showSubscriberBreak) {
    // truncate the html if subscriber gate is present and not logged in
    processorList.unshift(processors.processSubscriberGate);
  }

  if (showPaywallBreak) {
    // truncate the html if paywall break is present and not premium subscriber
    // or post is hidden due to being premium
    processorList.unshift(processors.truncateAfterPaywallBreak);
  }

  if (onlyRenderComments) {
    return null;
  }

  const processedElement = process(node, processorList, processorOptions);

  return (
    <>
      {showTextToSpeechBlock && post?.text_to_speech_config ? (
        <PostAudio textToSpeechConfig={post?.text_to_speech_config} />
      ) : null}
      {showPaywallAndHidePostContent && (
        <PaywallElement
          theme={paywall?.theme}
          url={paywallUrl}
          element={{
            type: 'paywall_break',
            attrs: {
              id: paywall?.id || 'post-page-paywall',
              data: {
                title: paywall?.title,
                description: paywall?.description,
                cta_text: paywall?.cta_text,
                benefits: paywall?.benefits,
                subheader: paywall?.subheader,
                image_url: paywall?.image_url,
                style: paywall?.style,
              },
            },
            content: [{ id: 'text', text: '' }],
          }}
        >
          {null}
        </PaywallElement>
      )}
      {!showPaywallAndHidePostContent && (
        <PostContentElement
          id="content-blocks"
          webTheme={{
            ...site.publication.web_theme,
            text_on_background_color: node.attrs.color || site.publication.web_theme.text_on_background_color,
          }}
          postTheme={postTheme as PostTheme}
          element={processedElement}
          isLive
          translations={{
            'login.already_a_subscriber': t('login.already_a_subscriber'),
            'shared.cta.sign_in': t('shared.cta.sign_in'),
            'login.not_now': t('login.not_now'),
            'shared.actions.submitting': t('shared.actions.submitting'),
            'subscribe.email_placeholder': t('subscribe.email_placeholder'),
            'shared.cta.subscribe': t('shared.cta.subscribe'),
            'shared.cta.login': t('shared.cta.login'),
            'shared.cta.or': t('shared.cta.or'),
            'shared.cta.to_participate': t('shared.cta.to_participate'),
            'shared.poll.subscribe_text': t('shared.poll.subscribe_text'),
            'shared.image.missing_image': t('shared.image.missing_image'),
          }}
          rewriteImageSrc={rewriteSrc}
          loginPath="/login"
          subscribePath="/subscribe"
          homePath="/"
          upgradePath="/upgrade"
          showSubscriberBreak={showSubscriberBreak}
          showPaywallBreak={showPaywallBreak}
          ChildDecoratorComponent={ChildDecoratorComponent}
          isIndexingDisabled={site?.viewable_site_version?.metadata?.noindex_enabled === true} // replaces nofollow|noreferrer|noopener in the value of anchors' rel attribute
          utmParams={utmParams}
          isSubscriberAuthenticated={isLoggedIn}
        >
          {children}
        </PostContentElement>
      )}
    </>
  );
};

export { PostContent };
